import {
  TARE_PROFILE_VALIDATION_FAILED,
  UNIT_PROFILE_VALIDATION_FAILED,
  type HoldingUnitCapabilities,
  type HoldingUnitLifecycleStatus,
  type HoldingUnitProfile,
  type TareProfileMode,
  type UnitProfileCategory,
  type UnitProfileFixedIdPolicy,
  type UnitProfileTemplateKey,
} from '@crv/contracts';

import { deriveHoldingUnitCapabilities } from '../../holding-units/domain/holding-unit-capabilities.js';
import { TareProfileDomainError, UnitProfileDomainError } from './errors.js';
import {
  assertTareProfileValid,
  deriveUnitProfileCapabilityPolicy,
  validateTareProfile,
  validateUnitProfile,
  validateUnitProfileTareCompatibility,
  type UnitProfileValidationInput,
} from './validation.js';

export interface UnitProfileResolutionInput extends UnitProfileValidationInput {
  id: string;
  displayName: string;
  revision: number;
  tareProfileId?: string | null;
}

export interface TareProfileResolutionInput {
  id: string;
  mode: TareProfileMode;
  defaultTareLb?: number | null;
  allowOperatorOverride: boolean;
  requireOperatorConfirmation: boolean;
  revision: number;
}

export type ResolvedTareSource =
  | 'none'
  | 'profile_default'
  | 'operator_required'
  | 'fixed_asset_master';

export interface ResolvedTareProfile {
  tareProfileId: string | null;
  tareProfileRevision: number | null;
  mode: TareProfileMode;
  source: ResolvedTareSource;
  defaultTareLb: number | null;
  tareSuggestedLb: number | null;
  requiresOperatorTare: boolean;
  allowOperatorOverride: boolean;
  requireOperatorConfirmation: boolean;
}

export interface ResolvedUnitProfile {
  unitProfileId: string;
  unitProfileRevision: number;
  unitProfileDisplayName: string;
  unitProfileTemplateKey: UnitProfileTemplateKey;
  profileCategory: UnitProfileCategory;
  unitType: HoldingUnitProfile['unitType'];
  identityMode: HoldingUnitProfile['identityMode'];
  containerKind: HoldingUnitProfile['containerKind'];
  productKind: HoldingUnitProfile['productKind'];
  fixedIdPolicy: UnitProfileFixedIdPolicy;
  capabilityPolicy: HoldingUnitCapabilities;
  effectiveCapabilities: HoldingUnitCapabilities;
  tare: ResolvedTareProfile;
}

export interface ResolveUnitProfileInput {
  profile: UnitProfileResolutionInput;
  tareProfile?: TareProfileResolutionInput | null;
  lifecycleStatus?: HoldingUnitLifecycleStatus;
  fixedAssetMasterTareLb?: number | null;
}

export function resolveUnitProfile(input: ResolveUnitProfileInput): ResolvedUnitProfile {
  const profileResult = validateUnitProfile(input.profile);
  if (!profileResult.isValid) {
    throw new UnitProfileDomainError(
      UNIT_PROFILE_VALIDATION_FAILED,
      'Unit profile failed validation.',
      profileResult,
    );
  }

  const tareCompatibilityResult = validateUnitProfileTareCompatibility(
    input.profile,
    input.tareProfile,
  );
  if (!tareCompatibilityResult.isValid) {
    throw new UnitProfileDomainError(
      UNIT_PROFILE_VALIDATION_FAILED,
      'Unit profile tare configuration is invalid.',
      tareCompatibilityResult,
    );
  }

  const capabilityPolicy = deriveUnitProfileCapabilityPolicy(
    input.profile.templateKey,
    input.profile.capabilityPolicy,
  );
  const effectiveCapabilities = deriveUnitProfileEffectiveCapabilities({
    profile: input.profile,
    capabilityPolicy,
    lifecycleStatus: input.lifecycleStatus ?? 'open',
  });

  return {
    unitProfileId: input.profile.id,
    unitProfileRevision: input.profile.revision,
    unitProfileDisplayName: input.profile.displayName,
    unitProfileTemplateKey: input.profile.templateKey as UnitProfileTemplateKey,
    profileCategory: input.profile.profileCategory as UnitProfileCategory,
    unitType: input.profile.unitType,
    identityMode: input.profile.identityMode,
    containerKind: input.profile.containerKind,
    productKind: input.profile.productKind,
    fixedIdPolicy: input.profile.fixedIdPolicy as UnitProfileFixedIdPolicy,
    capabilityPolicy,
    effectiveCapabilities,
    tare: resolveTareProfile({
      tareProfile: input.tareProfile,
      fixedAssetMasterTareLb: input.fixedAssetMasterTareLb,
    }),
  };
}

export function deriveUnitProfileEffectiveCapabilities(input: {
  profile: HoldingUnitProfile;
  capabilityPolicy: HoldingUnitCapabilities;
  lifecycleStatus?: HoldingUnitLifecycleStatus;
}): HoldingUnitCapabilities {
  const lifecycleCapabilities = deriveHoldingUnitCapabilities(
    input.profile,
    input.lifecycleStatus ?? 'open',
  );

  return andCapabilities(lifecycleCapabilities, input.capabilityPolicy);
}

export function resolveTareProfile(input: {
  tareProfile?: TareProfileResolutionInput | null;
  fixedAssetMasterTareLb?: number | null;
}): ResolvedTareProfile {
  if (!input.tareProfile) {
    return {
      tareProfileId: null,
      tareProfileRevision: null,
      mode: 'none',
      source: 'none',
      defaultTareLb: null,
      tareSuggestedLb: null,
      requiresOperatorTare: false,
      allowOperatorOverride: false,
      requireOperatorConfirmation: false,
    };
  }

  const validation = validateTareProfile(input.tareProfile);
  if (!validation.isValid) {
    throw new TareProfileDomainError(
      TARE_PROFILE_VALIDATION_FAILED,
      'Tare profile failed validation.',
      validation,
    );
  }

  assertTareProfileValid(input.tareProfile);

  switch (input.tareProfile.mode) {
    case 'none':
      return buildResolvedTare(input.tareProfile, 'none', null, null, false);
    case 'manual_required':
      return buildResolvedTare(
        input.tareProfile,
        'operator_required',
        null,
        null,
        true,
      );
    case 'fixed_default':
      return buildResolvedTare(
        input.tareProfile,
        'profile_default',
        input.tareProfile.defaultTareLb ?? null,
        input.tareProfile.defaultTareLb ?? null,
        false,
      );
    case 'fixed_asset_master':
      if (input.fixedAssetMasterTareLb == null) {
        throw new TareProfileDomainError(
          TARE_PROFILE_VALIDATION_FAILED,
          'fixed_asset_master tare resolution requires fixedAssetMasterTareLb.',
          { tareProfileId: input.tareProfile.id },
        );
      }
      return buildResolvedTare(
        input.tareProfile,
        'fixed_asset_master',
        null,
        input.fixedAssetMasterTareLb,
        false,
      );
    default:
      return assertNever(input.tareProfile.mode);
  }
}

function buildResolvedTare(
  profile: TareProfileResolutionInput,
  source: ResolvedTareSource,
  defaultTareLb: number | null,
  tareSuggestedLb: number | null,
  requiresOperatorTare: boolean,
): ResolvedTareProfile {
  return {
    tareProfileId: profile.id,
    tareProfileRevision: profile.revision,
    mode: profile.mode,
    source,
    defaultTareLb,
    tareSuggestedLb,
    requiresOperatorTare,
    allowOperatorOverride: profile.allowOperatorOverride,
    requireOperatorConfirmation: profile.requireOperatorConfirmation,
  };
}

function andCapabilities(
  left: HoldingUnitCapabilities,
  right: HoldingUnitCapabilities,
): HoldingUnitCapabilities {
  return {
    canReceiveIntake: left.canReceiveIntake && right.canReceiveIntake,
    canReceiveTransfer: left.canReceiveTransfer && right.canReceiveTransfer,
    canTransferOut: left.canTransferOut && right.canTransferOut,
    canAdjust: left.canAdjust && right.canAdjust,
    canReady: left.canReady && right.canReady,
    canShip: left.canShip && right.canShip,
    canRetire: left.canRetire && right.canRetire,
    canBuildProduct: left.canBuildProduct && right.canBuildProduct,
    canHoldLeftover: left.canHoldLeftover && right.canHoldLeftover,
    canReopen: left.canReopen && right.canReopen,
    canDisassemble: left.canDisassemble && right.canDisassemble,
  };
}

function assertNever(value: never): never {
  throw new TareProfileDomainError(
    TARE_PROFILE_VALIDATION_FAILED,
    'Unsupported tare profile mode.',
    { mode: value },
  );
}
